import { ServiceError } from '../errors'
import { readLocal, writeLocal } from '../local-store'
import { simulate } from '../mock-utils'
import { money } from '../types'
import { checkoutGuideItem, rebookUrl, saveChecked, withChecked } from './shared'
import type { CheckoutService } from './types'

/**
 * Mock de check-out y después (Cierre.dc.html checkout y post). Códigos:
 *  - `salida-falla` → "Ya hemos salido" falla · `ya-salido` → salida ya avisada
 *  - `resena-hecha` → reseña ya dejada · `sin-fidelizacion` → la propiedad no tiene programa
 *  - `pendiente-pago` → queda algo por pagar · `error-checkout`, `offline-checkout`, `lento-checkout`
 */
export const LEFT_FIELD = 'checkoutLeftAt'

const CHECKLIST = [
  { id: 'ventanas', text: 'Ventanas cerradas y climatización apagada' },
  { id: 'basura', text: 'Basura en el contenedor de la esquina' },
  { id: 'puerta', text: 'Puerta cerrada al salir (se bloquea sola)' },
]
const CHECKLIST_HOTEL = [
  { id: 'llave', text: 'Tarjeta de la habitación en recepción o en el buzón de salida' },
  { id: 'caja', text: 'Caja fuerte abierta y vacía' },
]

export const checkoutMock: CheckoutService = {
  async getCheckout(stay, _lang, signal) {
    await simulate(stay.guestCode, 'checkout', signal)
    const g = stay.guestCode
    const hotel = g.includes('hotel')
    const leftAt = readLocal<string>(g, LEFT_FIELD) ?? (g.includes('ya-salido') ? stay.dates.checkOut : null)
    const charges = hotel
      ? [
          { id: 'C1', concept: 'Habitación Doble superior · 3 noches', amount: money(54000) },
          { id: 'C2', concept: 'Tasa turística', amount: money(1500) },
        ]
      : [
          { id: 'C1', concept: 'Alojamiento · 3 noches', amount: money(45000) },
          { id: 'C2', concept: 'Desayuno · 4 oct', amount: money(3600) },
          { id: 'C3', concept: 'Transfer', amount: money(4500) },
        ]
    return {
      time: stay.dates.checkOut,
      checklist: withChecked(g, hotel ? CHECKLIST_HOTEL : CHECKLIST),
      instructions: checkoutGuideItem(stay),
      left: leftAt !== null,
      leftAt,
      canMarkLeft: true,
      charges,
      total: money(charges.reduce((sum, c) => sum + c.amount.amount, 0), stay.currency),
      allPaid: !(hotel || g.includes('pendiente-pago')),
    }
  },
  setChecklistItem(stay, id, done) {
    saveChecked(stay.guestCode, id, done)
    return Promise.resolve()
  },
  async markLeft(stay) {
    await simulate(stay.guestCode, 'checkout')
    if (stay.guestCode.includes('salida-falla')) throw new ServiceError('server', 'mock left error')
    const leftAt = new Date().toISOString()
    writeLocal(stay.guestCode, LEFT_FIELD, leftAt)
    return { leftAt }
  },
  async getAfterStay(stay, signal) {
    await simulate(stay.guestCode, 'checkout', signal)
    const g = stay.guestCode
    const url = rebookUrl(stay) ?? 'https://bookingengine.availroom.com/?environment=DEMO'
    return {
      review: stay.channel.name ? { channelName: stay.channel.name, url: 'https://www.booking.com/reviews.html', done: g.includes('resena-hecha') } : null,
      rebook: { url, discountPercent: 10, suggestedDates: [{ from: '2026-12-05', to: '2026-12-08', label: 'puente' }, { from: '2027-04-02', to: '2027-04-05', label: 'Semana Santa' }] },
      loyalty: g.includes('sin-fidelizacion') || g.includes('hotel') ? null : { levelName: 'Huésped habitual', staysDone: 1, staysNeeded: 3, nextLevel: 'Plata', nextLevelPerks: 'salida tardía gratis y desayuno el primer día' },
    }
  },
}
